import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ISbStory } from '@storyblok/js';
import { map } from 'rxjs';
import { StoryblokHelperService } from '../../../services/storyblok-helper.service';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { PictureComponent } from '../../../shared/components/picture/picture.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { StoryblokCdnPipe } from '../../pipes/storyblok-cdn.pipe';
import { StoryblokImage } from '../../storyblok-types';

@Component({
  selector: 'app-new-blog-meta',
  templateUrl: './new-blog-meta.component.html',
  styleUrl: './new-blog-meta.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonDirective, SvgComponent, DatePipe, PictureComponent, ContextMenuComponent, StoryblokCdnPipe],
})
export class NewBlogMetaComponent {
  private route = inject(ActivatedRoute);
  private storyblokHelperService = inject(StoryblokHelperService);

  readonly story = input<ISbStory['data']['story']>();

  readonly storyLength = computed(() => {
    const story = this.story();
    if (!story) return 0;
    if (!story.content.body?.content) return 0;

    const textBlocks = this.storyblokHelperService.getContentTypes(story.content.body.content, ['text']);
    const allText = `${story.content.title} ${story.content.lead} ${textBlocks.map(b => b.text).join(' ')}`;
    const averageWordsPerMinute = 300; // Average reading speed
    const words = allText.split(/\s+/).filter(word => word.length > 0).length; // Split by whitespace and filter out empty strings
    const minutes = Math.round(words / averageWordsPerMinute);
    return minutes;
  });

  readonly meta = toSignal(
    this.route.data.pipe(
      map(data => {
        const authorId: string = data.story.content.author;

        const author: {
          name: string;
          role: string;
          url: string;
          image: StoryblokImage;
          xLink: string;
          instaLink: string;
        } = authorId ? data.relationships.find(r => r.uuid === authorId)?.content : undefined;

        const updated = data.story.first_published_at === data.story.published_at ? undefined : data.story.published_at;

        return {
          published: data.story.first_published_at ? new Date(data.story.first_published_at) : undefined,
          updated: updated ? new Date(updated) : undefined,
          author,
        };
      })
    )
  );
}
